<svelte:options
	customElement={{
		tag: 'rf-observer',
		shadow: 'open',
	}}
/>

<script module>
	type ReframeObserverProps = {
		debug?: boolean;
		'events-queue-delay'?: number;
	};

	interface EventDetail {
		markerOrder: number;
		[key: string]: any;
	}

	type EventType = 'enter' | 'leave';

	interface QueueItem {
		type: EventType;
		detail: EventDetail;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import { onMount, createEventDispatcher, tick } from 'svelte';
	import { get } from 'svelte/store';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import type { ReframeMarker } from '@core/rf-observer/rf-marker.svelte';
	import RfObserverTrigger from '@core/rf-observer/rf-observer-trigger.svelte';
	import debounced from '@src/utils/debounced';
	/**
	 * Props
	 */
	let { debug = false, 'events-queue-delay': eventsQueueDelay = 500 }: ReframeObserverProps = $props();
	/***
	 * Composables
	 */
	const dispatch = createEventDispatcher();
	const { element, forEachSlottedElement } = useSlottedElements();

	/**
	 * Variables
	 */
	let markers: ReframeMarker[] = $state([]);
	// process queues separately for enter and leave events to avoid conflicts
	let enterQueue: { type: 'enter'; detail: { markerOrder: number; [key: string]: any } }[] = [];
	let leaveQueue: { type: 'leave'; detail: { markerOrder: number; [key: string]: any } }[] = [];
	// flags to track the processing state of each queues
	let isProcessingEnterQueue = false;
	let isProcessingLeaveQueue = false;
	/***
	 * Methods
	 */
	const processQueue = async (type: EventType): Promise<void> => {
		// Limit the number of iterations to avoid infinite loops
		let maxIterations = 1000;
		let iterations = 0;
		// Get the queue and callback based on the event type
		const queue: QueueItem[] = type === 'enter' ? enterQueue : leaveQueue;
		let enterTimeout: ReturnType<typeof setTimeout> | null = null;
		let leaveTimeout: ReturnType<typeof setTimeout> | null = null;
		let queueLength = queue.length;

		// check if the queue is already processing
		if (type === 'enter') {
			if (isProcessingEnterQueue) return;
			isProcessingEnterQueue = true;
		} else {
			if (isProcessingLeaveQueue) return;
			isProcessingLeaveQueue = true;
		}

		// clear the timeout if it exists
		if (enterTimeout) clearTimeout(enterTimeout);
		if (leaveTimeout) clearTimeout(leaveTimeout);

		// process the queue
		while (queue.length > 0 && maxIterations > iterations) {
			const newEvent = queue.shift();
			if (newEvent) {
				// Get the marker enter and leave callbacks
				const { enter, leave } = newEvent.detail.marker.getMarkerCallback();

				// Dispatch the event
				dispatch(newEvent.type, newEvent.detail);

				// Wait for the delay before processing the next event
				await new Promise<void>((resolve) => {
					if (type === 'enter') {
						enterTimeout = setTimeout(
							() => {
								resolve();
								enter(newEvent.detail.attachedElement, newEvent.detail.direction);
							},
							queueLength === queue.length + 1 ? 0 : eventsQueueDelay
						);
					} else {
						leaveTimeout = setTimeout(resolve, eventsQueueDelay);
						leave(newEvent.detail.attachedElement, newEvent.detail.direction);
					}
				});
			}
			iterations++;
		}

		// reset the processing flag
		if (type === 'enter') {
			isProcessingEnterQueue = false;
		} else {
			isProcessingLeaveQueue = false;
		}
	};

	// debounced functions
	const debouncedProcessEnterQueue = debounced(() => processQueue('enter'), 10);
	const debouncedProcessLeaveQueue = debounced(() => processQueue('leave'), 10);

	const handleEvent = (type: EventType, detail: EventDetail): void => {
		// get the queue and callback based on the event type
		const queue: QueueItem[] = type === 'enter' ? enterQueue : leaveQueue;
		const callback = type === 'enter' ? debouncedProcessEnterQueue : debouncedProcessLeaveQueue;
		// Check if event is already in the queue
		if (queue.find((event) => event.detail.markerOrder === detail.markerOrder)) {
			return;
		}
		// push the event to the queue and sort it by marker order
		queue.push({ type, detail });
		// sort the queue by marker order
		queue.sort((a, b) => a.detail.markerOrder - b.detail.markerOrder);
		// run the callback
		callback();
	};

	const initObserver = (): void => {
		const currentElement = get(element);
		if (!currentElement) return;

		// get all the markers in the current element
		forEachSlottedElement((el) => {
			if (el.tagName === 'RF-MARKER') {
				markers = [...markers, el as ReframeMarker];
			} else {
				markers = [...markers, ...Array.from(el.querySelectorAll('rf-marker'))] as ReframeMarker[];
			}
		});

		markers.forEach((marker) => {
			// get the marker order
			const markerOrder = marker.getMarkerOrder();
			// add event listeners to the marker
			if (!marker['trigger-position']) {
				marker.addEventListener('enter', (e) => {
					handleEvent('enter', { ...(e as CustomEvent).detail, markerOrder, marker });
				});
				marker.addEventListener('leave', (e) => {
					handleEvent('leave', { ...(e as CustomEvent).detail, markerOrder, marker });
				});
			}
		});
	};
	/**
	 * Lifecycle
	 */
	onMount(async () => {
		// wait for the tick to initialize the observer
		await tick();
		initObserver();
	});
</script>

<section bind:this={$element} part="rf-observer" class="rf-observer">
	{#each markers as marker, index}
		<!-- Add trigger based on the provided trigger position on the marker -->
		{#if marker['trigger-position']}
			<RfObserverTrigger
				{debug}
				triggerPosition={parseFloat(String(marker['trigger-position'] ?? '')) || index / markers.length}
				rootMargin={marker['root-margin']}
				layoutId={marker['layout-id']}
				{...marker}
				on:enter={(e: CustomEvent) => {
					handleEvent('enter', { ...e.detail, markerOrder: marker.getMarkerOrder(), marker });
				}}
				on:leave={(e: CustomEvent) => {
					handleEvent('leave', { ...e.detail, markerOrder: marker.getMarkerOrder(), marker });
				}}
			/>
		{/if}
	{/each}
	<slot></slot>
</section>

<style lang="scss" global>
	.rf-observer {
		position: relative;
	}
</style>
